import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import { formatDate } from '../../utils/Utils';
import {
  CALIBRATION_STATUS_COLORS, StatusBadge,
} from './config';
import { GoalResultsSection, FeedbackSummarySection } from './CalibrationFindings';
import PerformanceContextPanel from '../performance-context/PerformanceContextPanel';

// Task 30 — yönetici kalibrasyon detay modalı: 360 özeti (yorumlar dahil),
// hedef sonuçları, sürekli GB referans paneli (varsa) + nihai seviye seçimi +
// Onayla. GoalDecisionModal.js (Task 27) ile aynı modal iskeleti/desen.
//
// P2 Faz 1 (Paket B) — sağa bağlam paneli eklendi: mevcut bulgular solda,
// PerformanceContextPanel sağda (kendi verisini kendi çeker; bu dosya ona
// yalnız periodId/employeeId geçirir). Dar ekranda tek kolona düşer.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 1080px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

// İki kolon: solda karar içeriği (bulgular + seviye seçimi), sağda bağlam
// paneli. 980px altında panel ALTA düşer (modal zaten dikey kaydırmalı).
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  min-width: 0;
`;

const DialogTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div`
  margin-bottom: 20px;
  padding-top: 16px;
  border-top: 1px solid #E8ECF0;
`;

const RefRow = styled.div`
  background: #F5F7FA;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
`;

const RefCategory = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  margin-right: 8px;
`;

const RefDate = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  float: right;
`;

const RefText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 4px;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #b02a37;
  margin-top: 6px;
`;

// P1 — birleşik A+B skoru kartı (CalibrationFindings'teki WeightedOverallCard
// görsel dilinin aynası; yalnız BE değer döndürdüyse — CombinedScoreEnabled —
// render edilir).
const CombinedScoreCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
`;

const CombinedScoreLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const CombinedScoreValue = styled.div`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.secondary.main};
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// PMF-NEW-01 — kurtarma bölümü. Normal karar akışından GÖRSEL olarak ayrılır
// (amber uyarı zemini): bu istisnai, denetim izine yazılan bir müdahaledir,
// gündelik bir buton değil.
const RecoveryBox = styled.div`
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 16px;
`;

const RecoveryTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #92400E;
  margin-bottom: 6px;
`;

const RecoveryHint = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #92400E;
  margin-bottom: 12px;
`;

const EmptyBox = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

export default function CalibrationDecisionModal({
  open, record, onClose, onApproved,
  getManagerView, getFeedbackSummary, getEmployeeGoals, managerApprove,
  // PMF-NEW-01 — kurtarma kapısı. canForce yalnız enterpriseAdmin/superAdmin
  // için true (sayfa AuthContext'ten geçirir); asıl kapı backend'dedir.
  canForce = false, forceApprove,
}) {
  const { t, i18n } = useTranslation();
  const [managerView, setManagerView] = useState(null);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [busy, setBusy] = useState(false);
  // PMF-NEW-01 — zorlama gerekçesi (backend trim sonrası boşsa 400 reddeder).
  const [forceReason, setForceReason] = useState('');
  const [forceOpen, setForceOpen] = useState(false);
  // Analitik Plan 5 — karar reddedilirse (ör. 409 versionConflict: kayıt başkası tarafından
  // değişti) görünüm GÜNCEL hâliyle yeniden çekilir; bayat seviyeyle kalınmaz.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open || !record) {
      setManagerView(null);
      setFeedbackSummary(null);
      setGoals([]);
      setSelectedLevel(null);
      setForceReason('');
      setForceOpen(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getManagerView(record.recordId),
      getFeedbackSummary(record.periodId, record.employeeId),
      getEmployeeGoals(record.periodId, record.employeeId),
    ]).then(([mv, fb, gl]) => {
      setManagerView(mv);
      setFeedbackSummary(fb);
      setGoals(gl || []);
      // PMF-15 — seçici, KAYDEDİLMİŞ değerle açılır. Kayıt AKTİF bir kalibrasyon
      // oturumuna bağlıysa yönetici onayı finalize DEĞİL ÖNERİ üretir ve backend
      // finalLevelId'yi DEĞİL proposedLevelId'yi yazar (FinalizeOrProposeAsync).
      // Yalnız finalLevelId okunduğu için yönetici "öner"e basıp sayfayı yeniden
      // açtığında seçiciyi BOŞ görüyor, farklı bir seviye seçip kendi önerisini
      // izsiz eziyordu. Öncelik finalLevelId'dedir: finalize edilmiş kayıtta
      // nihai karar öneriyi geçersiz kılar.
      setSelectedLevel(mv?.finalLevelId || mv?.proposedLevelId || null);
    }).finally(() => setLoading(false));
  }, [open, record, getManagerView, getFeedbackSummary, getEmployeeGoals, reloadKey]);

  // Plan 2 (Task 8) — seçici artık dönemin skalasından gelir (SortOrder
  // sıralı, ManagerViewDto.ScaleLevels); sabit üç token'lı liste kalktı.
  const levelOptions = useMemo(() => (
    [...(managerView?.scaleLevels || [])].sort((a, b) => a.sortOrder - b.sortOrder)
  ), [managerView]);

  const employeeApproved = managerView?.status !== 'invited';
  const alreadyFinalized = managerView?.status === 'manager_approved';
  const canApprove = employeeApproved && !alreadyFinalized && !!selectedLevel;

  const handleApprove = useCallback(async () => {
    if (!record || !canApprove) return;
    setBusy(true);
    const ok = await managerApprove(record.recordId, selectedLevel, managerView?.version);
    setBusy(false);
    if (ok) onApproved();
    else setReloadKey((k) => k + 1);
  }, [record, canApprove, managerApprove, selectedLevel, onApproved, managerView]);

  // PMF-NEW-01 — kurtarma YALNIZ el sıkışmanın TIKANDIĞI kayıtta anlamlı:
  // çalışan daveti yanıtlamadığı için normal yol "employeeFirst" ile kapalı.
  // "employee_approved" durumunda normal onay (PMF-13 admin bypass'ıyla) zaten
  // çalışır — orada zorlama GÖSTERİLMEZ, gereksiz bir istisna yolu açmamak için.
  const forceAvailable = canForce && !!forceApprove && managerView?.status === 'invited';

  const handleForceApprove = useCallback(async () => {
    if (!record || !selectedLevel || !forceReason.trim()) return;
    setBusy(true);
    const ok = await forceApprove(record.recordId, selectedLevel, forceReason.trim(), managerView?.version);
    setBusy(false);
    if (ok) onApproved();
    else setReloadKey((k) => k + 1);
  }, [record, selectedLevel, forceReason, forceApprove, onApproved, managerView]);

  if (!open) return null;

  const cfg = managerView ? (CALIBRATION_STATUS_COLORS[managerView.status] || CALIBRATION_STATUS_COLORS.invited) : null;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{t('performance.calibration.detailTitle')}</DialogTitle>

        {loading && <EmptyBox>{t('common.loading')}</EmptyBox>}

        {!loading && !managerView && <EmptyBox>{t('responseErrors.performance.calibration.notFound')}</EmptyBox>}

        {!loading && managerView && (
          <ContentGrid>
            <MainColumn>
              <SubTitle>
                <strong>{record?.employeeName}</strong>
                {record?.periodName && <span>— {record.periodName}</span>}
                {cfg && (
                  <StatusBadge color={cfg.color} bg={cfg.bg}>
                    {t(`performance.calibration.status.${managerView.status}`, { defaultValue: managerView.status })}
                  </StatusBadge>
                )}
              </SubTitle>

              {/* P1 — birleşik A+B skoru: yalnız değer geldiyse (bayrak açık +
                  en az bir sistemde veri var) gösterilir. */}
              {feedbackSummary?.combinedScore != null && (
                <Section>
                  <CombinedScoreCard>
                    <CombinedScoreLabel>{t('performance.calibration.combinedScore')}</CombinedScoreLabel>
                    <CombinedScoreValue>{feedbackSummary.combinedScore}</CombinedScoreValue>
                  </CombinedScoreCard>
                </Section>
              )}

              <Section>
                <FeedbackSummarySection summary={feedbackSummary} showComments t={t} />
              </Section>

              <Section>
                <GoalResultsSection goals={goals} t={t} />
              </Section>

              {managerView.continuousFeedbackReferences && managerView.continuousFeedbackReferences.length > 0 && (
                <Section>
                  <h4 style={{ margin: '0 0 12px' }}>{t('performance.calibration.continuousFeedbackTitle')}</h4>
                  {managerView.continuousFeedbackReferences.map((ref, i) => (
                    <RefRow key={i}>
                      <RefCategory>
                        {t(`performance.calibration.continuousFeedback.category.${ref.category}`, { defaultValue: ref.category })}
                      </RefCategory>
                      <RefDate>{formatDate(ref.createDate, i18n.language)}</RefDate>
                      {ref.text && <RefText>{ref.text}</RefText>}
                    </RefRow>
                  ))}
                </Section>
              )}

              <Section>
                <GenericSelect
                  label={t('performance.calibration.selectLevel')}
                  value={selectedLevel}
                  onChange={alreadyFinalized ? undefined : setSelectedLevel}
                  options={levelOptions}
                  idKey="id"
                  labelKey="label"
                  required
                />
                {!employeeApproved && (
                  <HintText>{t('responseErrors.performance.calibration.employeeFirst')}</HintText>
                )}
                {alreadyFinalized && (
                  <HintText style={{ color: '#16A34A' }}>{t('performance.calibration.alreadyFinalized')}</HintText>
                )}
                {/* PMF-15 — kaydedilmiş öneri AÇIKÇA söylenir. Seçiciyi doldurmak
                    tek başına yetmez: yönetici seçilmiş değerin kendi ÖNERİSİ mi
                    yoksa varsayılan mı olduğunu ayırt edemezdi. Yalnız finalize
                    EDİLMEMİŞ kayıtta gösterilir (finalize sonrası alttaki yeşil
                    satır zaten doğru bilgiyi verir). */}
                {!alreadyFinalized && managerView?.proposedLevelId && (
                  <HintText data-role="proposed-level-hint" style={{ color: '#0291EF' }}>
                    {t('performance.calibration.proposalSavedHint', {
                      level: managerView.proposedLevelLabel || '',
                    })}
                  </HintText>
                )}

                {/* PMF-NEW-01 — kalibrasyon kurtarma: davete hiç yanıt vermeyen
                    tek bir çalışan tüm dönemi süresiz kilitliyordu. Gerekçe
                    ZORUNLU; kim/ne/neden PerformanceAuditLog'a yazılır. */}
                {forceAvailable && (
                  <RecoveryBox>
                    <RecoveryTitle>{t('performance.calibration.forceTitle')}</RecoveryTitle>
                    <RecoveryHint>{t('performance.calibration.forceHint')}</RecoveryHint>
                    {!forceOpen ? (
                      <Button variant="outline" onClick={() => setForceOpen(true)}>
                        {t('performance.calibration.forceApprove')}
                      </Button>
                    ) : (
                      <>
                        <TextInput
                          label={t('performance.calibration.forceReason')}
                          type="textarea"
                          rows={3}
                          value={forceReason}
                          onChange={(e) => setForceReason(e.target.value)}
                          required
                        />
                        <div style={{ marginTop: 12 }}>
                          <Button
                            onClick={handleForceApprove}
                            disabled={!selectedLevel || !forceReason.trim() || busy}
                            loading={busy}
                          >
                            {t('performance.calibration.forceConfirm')}
                          </Button>
                        </div>
                      </>
                    )}
                  </RecoveryBox>
                )}
              </Section>
            </MainColumn>

            <PerformanceContextPanel periodId={record?.periodId} employeeId={record?.employeeId} />
          </ContentGrid>
        )}

        <Actions>
          <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
          {!alreadyFinalized && (
            <Button onClick={handleApprove} disabled={!managerView || !canApprove || busy} loading={busy}>
              {/* Faz B (spec 2026-08-07 §2.3/§6, Task 6) — kayıt AKTİF bir
                  kalibrasyon oturumuna bağlıysa (managerView.SessionId,
                  yalnız session.Status=="active" iken dolu — bkz.
                  CalibrationDataAccess.GetManagerViewAsync yorumu) bu buton
                  fiilen ManagerApproveAsync'in ÖNERİ dalına düşer (finalize
                  DEĞİL) — metin buna göre "Seviye Öner"; oturumsuz kayıtta
                  bire bir eski metin ("Onayla"). */}
              {managerView?.sessionId
                ? t('performance.calibration.proposeLevel')
                : t('performance.calibration.approve')}
            </Button>
          )}
        </Actions>
      </ModalCard>
    </Modal>
  );
}
